import { useLayoutEffect, useRef, useState } from "react";

/**
 * DOM measurements the sticky table layout needs: the measured widths of sticky `<th>` elements
 * (for the cumulative `left` offsets) and the visible width of the horizontal scroll viewport
 * (to keep the empty state centered in view on very wide tables). Extracted from {@link Table}
 * to keep that file under the size cap.
 */
export const useTableStickyMeasurements = () => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const thRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());
	const [measuredWidths, setMeasuredWidths] = useState<Record<string, number>>({});
	const [viewportWidth, setViewportWidth] = useState<number | undefined>(undefined);

	// Measure actual widths of sticky <th> elements after render.
	useLayoutEffect(() => {
		const newWidths: Record<string, number> = {};
		let changed = false;

		thRefs.current.forEach((el, uid) => {
			const w = el.offsetWidth;

			if (w > 0 && measuredWidths[uid] !== w) {
				newWidths[uid] = w;
				changed = true;
			} else if (measuredWidths[uid]) {
				newWidths[uid] = measuredWidths[uid];
			}
		});

		if (changed) {
			setMeasuredWidths(newWidths);
		}
	});

	// Track the visible width of the scroll viewport so the empty state can be pinned to it.
	useLayoutEffect(() => {
		const el = scrollContainerRef.current;

		if (!el) {
			return;
		}

		const measure = () => setViewportWidth(el.clientWidth);

		measure();

		const observer = new ResizeObserver(measure);

		observer.observe(el);

		return () => observer.disconnect();
	}, []);

	return { scrollContainerRef, thRefs, measuredWidths, viewportWidth };
};
